import { useFetch } from "@/hooks/useFetch";
import { getPlans } from "@/api/plans";
import type { Plan } from "@/types/api";
import { event as gaEvent } from "@/lib/gtag";

export default function Pricing() {
  const { data: plans, loading } = useFetch(getPlans);

  const handleApplyClick = (e: React.MouseEvent, planCode?: string) => {
    e.preventDefault();
    gaEvent('click_cta', { location: 'pricing', button: 'start', plan: planCode || '' });
    window.REACT_APP_NAVIGATE('/login', { state: { from: '/order' } });
  };

  return (
    <section id="pricing" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <p className="text-[#C9A96E] text-xs tracking-[0.4em] uppercase mb-4 font-light">Pricing</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-[#2D2D2D] mb-5">
            합리적인 가격으로<br />
            <span className="italic">특별한 순간을 담으세요</span>
          </h2>
          <p className="text-gray-500 text-sm font-light max-w-xl mx-auto leading-relaxed">
            전통 웨딩 촬영 비용의 일부로 고품질 AI 웨딩 스냅을 만나보세요.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-3xl text-[#C9A96E]"></i>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {(plans || []).map((plan) => {
              const isPopular = plan.popular === "y";
              return (
                <div
                  key={plan.code}
                  className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 ${
                    isPopular
                      ? 'bg-[#C9A96E] text-white shadow-2xl scale-105'
                      : 'bg-[#FDFAF6] border border-[#F0E8DC] hover:shadow-xl'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-[#C9A96E] text-xs font-medium px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
                      가장 인기있는 플랜
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className={`font-serif text-2xl font-medium mb-4 ${isPopular ? 'text-white' : 'text-[#2D2D2D]'}`}>
                      {plan.name}
                    </h3>
                    {plan.originalPrice > plan.price && (
                      <div className="mb-1">
                        <span className={`text-sm line-through ${isPopular ? 'text-white/50' : 'text-gray-400'}`}>
                          {plan.originalPrice.toLocaleString()}원
                        </span>
                        {plan.discountRate > 0 && (
                          <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                            isPopular ? 'bg-white/20 text-white' : 'bg-red-50 text-red-500'
                          }`}>
                            {plan.discountRate}% OFF
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-end justify-center gap-1 mb-2">
                      <span className={`font-serif text-4xl font-medium ${isPopular ? 'text-white' : 'text-[#2D2D2D]'}`}>
                        {plan.price.toLocaleString()}원
                      </span>
                    </div>
                    <p className={`text-xs font-light ${isPopular ? 'text-white/80' : 'text-gray-400'}`}>
                      1회 결제
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <i className={`ri-calendar-check-line text-lg shrink-0 ${isPopular ? 'text-white' : 'text-[#C9A96E]'}`}></i>
                      <span className={`text-sm font-medium ${isPopular ? 'text-white' : 'text-[#2D2D2D]'}`}>
                        서비스 제공기간 3개월
                      </span>
                    </li>
                    {plan.features
                      .filter((f) => !f.includes('스냅 사진'))
                      .map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <i className={`ri-check-line text-lg shrink-0 ${isPopular ? 'text-white' : 'text-[#C9A96E]'}`}></i>
                        <span className={`text-sm font-light ${isPopular ? 'text-white/90' : 'text-gray-600'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={(e) => handleApplyClick(e, plan.code)}
                    className={`w-full py-3.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      isPopular
                        ? 'bg-white text-[#C9A96E] hover:bg-gray-50'
                        : 'bg-[#C9A96E] text-white hover:bg-[#b8935a]'
                    }`}
                  >
                    시작하기
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-center gap-6 mt-10 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-refresh-line text-[#C9A96E]"></i>
            <span className="font-light">각 사진당 <strong className="font-medium text-[#2D2D2D]">2번 무료 재생성</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-calendar-check-line text-[#C9A96E]"></i>
            <span className="font-light">서비스 제공기간 <strong className="font-medium text-[#2D2D2D]">3개월</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="ri-time-line text-[#C9A96E]"></i>
            <span className="font-light">당일 결과물 수령</span>
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 font-light">
          * 오픈 기념 특별 할인가입니다. 모든 플랜은 VAT 포함 가격이며, 결제 후 환불은 생성 시작 전까지 가능합니다.
          <br />
          * 서비스 제공기간(재생성 및 다운로드 가능 기한)은 결제일로부터 3개월입니다.
        </p>
      </div>
    </section>
  );
}
